import * as React from 'react';
import {styledDiv, styled, emotionStyled} from '@packages/utilities/styled';
import {map as mapValues} from '@packages/utilities/key-map';
import {CaretDownMedium, CaretRightMedium} from '@packages/systems/ui/icons';
import SVGButton from '@packages/systems/design-system/SVGButton';

import {Label} from '@packages/systems/design-system/Text';

import {usePlugins} from '@packages/utilities/platform';
import {type Type, Types, Plugins} from '@packages/systems/wfdl';
import {
  TBoolean,
  TNumber,
  TText,
  TRecord,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {type Json, matchJson, getObject} from '@packages/utilities/json';
import {typeName, qualify} from '@packages/systems/wfdl-Qualified';
import {FlowTabsControl} from '../FlowTabsControl';

// Using `import {TJson} from '@packages/systems/plugins/Json` was causing an issue
// during the test run of `@packages/systems/plugins/Http/GuessJsonParser.test.js`
const TJson = TTypeConstructor(qualify(Plugins.name('Json'), typeName('Json')));

/** Function for returning a WFDL Type from a JSON-type value */
const getTypeForJson: (arg1: Json) => Type = matchJson({
  Null: () => TJson,
  Boolean: () => TBoolean,
  Number: () => TNumber,
  String: () => TText,
  Array: () => TJson,
  Object: (theObject) => {
    const mappedTypes = mapValues(getTypeForJson)(theObject);
    return TRecord(mappedTypes);
  },
});

const TabControlWrapper = styledDiv(
  ({theme}) => ({
    flexGrow: 1,
    marginTop: theme.gutters.x16,
    display: 'flex',
    flexDirection: 'column',
  }),
  {
    nested: {marginTop: 0},
  }
);

const TypeViewItemContainer = styledDiv(
  ({theme, canExpand}) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(3, minmax(0px, max-content))`,
    gridColumnGap: theme.gutters.x8,
    alignItems: 'center',
    lineHeight: `${theme.typography.copy}px`,
    marginBottom: theme.gutters.x12,
    paddingLeft: canExpand ? 0 : `${CARET_WIDTH + 3}px`,

    cursor: 'default',
    userSelect: 'none',
  }),
  {
    canExpand: {
      gridTemplateColumns: `repeat(2, minmax(0px, max-content)) 1fr`,
    },
  }
);

const StyledOutputContainer = styledDiv(
  ({theme}) => ({
    background: theme.colors.backgroundVivid,
    padding: `${theme.gutters.x16}px`,
    marginTop: theme.gutters.x8,
    border: `1px solid ${theme.colors.borderVivid}`,
    borderRadius: theme.control.borderRadius,
    overflow: 'auto',
    height: '342px',
    width: '100%',
  }),
  {
    nested: ({theme: {gutters}, activeTabId}) => {
      const nestedStyle = {
        width: 'auto',
        marginLeft: gutters.x8,
        marginRight: gutters.x8,
      } as const;
      if (activeTabId === 'json_preview') {
        return {
          ...nestedStyle,
          padding: gutters.x8,
        };
      }
      return {
        ...nestedStyle,
        paddingTop: gutters.x8,
        paddingLeft: gutters.x8,
      };
    },
  }
);

const StyledFinePrint = styledDiv(
  ({theme}) => ({
    lineHeight: theme.typography.label.lineHeight,
    fontSize: theme.typography.finePrint.fontSize,

    color: theme.colors.foregroundDim,
  }),
  {
    truncate: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  }
);

const StyledJSONOutput = styled('pre')(({theme}) => ({
  lineHeight: `${theme.lineHeights.subheading}px`,
  // The dialog this is being rendered by is using designerThemeLarge
  fontSize: '11px',
  color: theme.colors.foreground,
  whiteSpace: 'pre',
  overflow: 'visible',
  margin: 0,
}));

const IconWrapper = styledDiv(({theme, hovered}) => ({
  color: hovered ? theme.colors.white : theme.colors.foregroundSlightlyDim,
  display: 'flex',
  alignItems: 'center',
  columnGap: theme.gutters.x4,
}));

const CARET_WIDTH = 8;
const CaretWrapper = emotionStyled('div')({
  width: `${CARET_WIDTH}px`,
});
const NestedOutputContainer = styledDiv(({theme}) => ({
  gridRow: 2,
  gridColumn: '1 / 4',
  marginTop: theme.gutters.x12,
  marginLeft: `${CARET_WIDTH / 2}px`,
  paddingLeft: theme.gutters.x16,

  borderLeft: `1px solid ${theme.colors.borderVivid}`,
  cursor: 'default',
  userSelect: 'none',
}));

function TypeViewItem({
  fieldName = '',
  value,
}: {
  fieldName?: string;
  value: Json;
}) {
  const plugins = usePlugins();
  const qualifiedPluginIcon = Types.getIcon(getTypeForJson(value), plugins);
  const [expanded, setExpanded] = React.useState(false);

  const jsonObject = getObject(value);
  const jsonObjectHasKeys = jsonObject
    ? Object.keys(jsonObject).length > 0
    : null;
  const stringifiedValue = JSON.stringify(value);

  return (
    <>
      <TypeViewItemContainer canExpand={jsonObjectHasKeys}>
        <IconWrapper>
          {jsonObjectHasKeys ? (
            <SVGButton
              onClick={() => setExpanded(!expanded)}
              icon={
                <CaretWrapper>
                  {expanded ? <CaretDownMedium /> : <CaretRightMedium />}
                </CaretWrapper>
              }
              data-automation-id="output-preview-expand-collapse-button"
            />
          ) : null}
          <Plugins.PluginIcon icon={qualifiedPluginIcon} />
        </IconWrapper>

        {/* @ts-expect-error - TS2769 - No overload matches this call. */}
        <Label data-automation-id={`output-field_${fieldName}`} dynamic>
          {fieldName}
        </Label>

        {/** This renders the value for the field for one of our primitive types */}
        {jsonObject ? null : (
          <StyledFinePrint truncate={stringifiedValue.length > 265}>
            {stringifiedValue}
          </StyledFinePrint>
        )}
      </TypeViewItemContainer>

      {/** We'll render the contents of a nested record here */}
      {jsonObject && expanded ? (
        <NestedOutputContainer>
          {/* @ts-expect-error - TS2786 - 'TypeView' cannot be used as a JSX component. */}
          <TypeView json={value} />
        </NestedOutputContainer>
      ) : null}
    </>
  );
}

function TypeView({json}: {json: Json}) {
  const jsonObject = getObject(json);

  // For an object, we iterate through it and get its fields
  if (jsonObject) {
    // @ts-expect-error - TS2345 - Argument of type '([fieldName]: [any], index: number) => Element' is not assignable to parameter of type '(value: [string, Json], index: number, array: [string, Json][]) => Element'.
    return Object.entries(jsonObject).map(([fieldName]: [any], index) => {
      return (
        <TypeViewItem
          key={index}
          fieldName={fieldName}
          // @ts-expect-error - TS2322 - Type 'Json | undefined' is not assignable to type 'Json'.
          value={jsonObject[fieldName]}
        />
      );
    });
  }

  // Otherwise, this was a primitive type that we can display as-is
  return <TypeViewItem value={json} />;
}

const tabStyle = (theme: any) => ({
  fontSize: theme.fontSizes.subheading,
});

const StyledFlowTabsControl = styled(FlowTabsControl)(
  {
    paddingLeft: 0,
    paddingRight: 0,
  },
  {
    nested: ({theme: {gutters}}) => ({
      paddingLeft: gutters.x8,
      paddingRight: gutters.x8,
    }),
  }
);

export const OutputPreview = React.memo<{
  outputJson: Json;
  nested?: boolean;
  outputLabel?: string;
}>(function OutputPreview({
  outputJson,
  nested = false,
  outputLabel = 'Output preview',
}) {
  const TABS_LIST = React.useMemo(
    () => ({
      data_output: {
        label: outputLabel,
        style: tabStyle,
      },
      json_preview: {
        label: 'JSON',
        style: tabStyle,
      },
    }),
    [outputLabel]
  );

  return (
    <TabControlWrapper nested={nested} data-automation-id="output-preview-tabs">
      <StyledFlowTabsControl
        nested={nested}
        tabs={TABS_LIST}
        defaultValue="data_output"
      >
        {/* @ts-expect-error - TS7006 - Parameter 'activeTabId' implicitly has an 'any' type. */}
        {(activeTabId) => {
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ data_output: { label: string; style: (theme: any) => { fontSize: any; }; }; json_preview: { label: string; style: (theme: any) => { fontSize: any; }; }; }'.
          switch (TABS_LIST[activeTabId]) {
            case TABS_LIST.data_output: {
              return (
                <StyledOutputContainer
                  className="kit-scroll-pane kit-scrollbar with-horizontal allow-horizontal-swipe"
                  nested={nested}
                >
                  {/* @ts-expect-error - TS2786 - 'TypeView' cannot be used as a JSX component. */}
                  <TypeView json={outputJson} />
                </StyledOutputContainer>
              );
            }

            case TABS_LIST.json_preview: {
              return (
                <StyledOutputContainer
                  className="kit-scroll-pane kit-scrollbar with-horizontal allow-horizontal-swipe"
                  nested={nested}
                >
                  <StyledJSONOutput>
                    {JSON.stringify(outputJson, undefined, 2)}
                  </StyledJSONOutput>
                </StyledOutputContainer>
              );
            }
          }

          return null;
        }}
      </StyledFlowTabsControl>
    </TabControlWrapper>
  );
});
